/** Filtros opcionales GET `/courier-settlements/:id/lines` y `/lines/export`. */
export interface CourierSettlementLinesFilters {
  search?: string;
  /** Prioridad sobre match_status en backend */
  matched?: boolean;
  shop_ids?: number[];
  team_ids?: number[];
  order_status_ids?: number[];
  shipment_status_ids?: number[];
  /** facturado | por facturar | no facturado */
  estados?: string[];
  weight_compares?: SettlementWeightCompare[];
}

export type SettlementWeightCompare = 'courier_gt_ours' | 'courier_lt_ours' | 'equal' | 'distinct';

export const BILLING_FILTER_VALUES = ['facturado', 'por facturar', 'no facturado'] as const;

export const WEIGHT_COMPARE_VALUES: SettlementWeightCompare[] = [
  'courier_gt_ours',
  'courier_lt_ours',
  'equal',
  'distinct',
];

export const EMPTY_SETTLEMENT_LINES_FILTERS: CourierSettlementLinesFilters = {};

export function buildSettlementLinesQueryParams(
  filters: CourierSettlementLinesFilters,
  pagination?: { page: number; per_page: number },
): Record<string, string | number | boolean | number[] | string[]> {
  const out: Record<string, string | number | boolean | number[] | string[]> = {};
  if (pagination) {
    out.page = pagination.page;
    out.per_page = pagination.per_page;
  }

  const search = filters.search?.trim();
  if (search) {
    out.search = search.slice(0, 255);
  }

  if (filters.matched === true || filters.matched === false) {
    out.matched = filters.matched;
  }

  if (filters.shop_ids?.length) {
    out.shop_ids = [...filters.shop_ids];
  }
  if (filters.team_ids?.length) {
    out.team_ids = [...filters.team_ids];
  }
  if (filters.order_status_ids?.length) {
    out.order_status_ids = [...filters.order_status_ids];
  }
  if (filters.shipment_status_ids?.length) {
    out.shipment_status_ids = [...filters.shipment_status_ids];
  }
  if (filters.estados?.length) {
    out.estados = [...filters.estados];
  }
  if (filters.weight_compares?.length) {
    out.weight_compares = [...filters.weight_compares];
  }

  return out;
}

/** Serializa params para Laravel (`shop_ids[]=1`, `estados[]=por%20facturar`, …). */
export function serializeSettlementLinesParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === null) continue;
    if (Array.isArray(val)) {
      for (const item of val) {
        if (item === undefined || item === null || item === '') continue;
        parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`);
      }
    } else if (typeof val === 'boolean') {
      parts.push(`${encodeURIComponent(key)}=${val ? '1' : '0'}`);
    } else {
      const s = String(val);
      if (s === '') continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(s)}`);
    }
  }
  return parts.join('&');
}

export function serializeSettlementLinesFiltersKey(filters: CourierSettlementLinesFilters): string {
  return JSON.stringify({
    search: filters.search?.trim() ?? '',
    matched: filters.matched === true ? '1' : filters.matched === false ? '0' : '',
    shop_ids: [...(filters.shop_ids ?? [])].sort((a, b) => a - b).join(','),
    team_ids: [...(filters.team_ids ?? [])].sort((a, b) => a - b).join(','),
    order_status_ids: [...(filters.order_status_ids ?? [])].sort((a, b) => a - b).join(','),
    shipment_status_ids: [...(filters.shipment_status_ids ?? [])].sort((a, b) => a - b).join(','),
    estados: [...(filters.estados ?? [])].sort().join('|'),
    weight_compares: [...(filters.weight_compares ?? [])].sort().join('|'),
  });
}

export function countActiveSettlementLinesFilters(filters: CourierSettlementLinesFilters): number {
  let n = 0;
  if (filters.search?.trim()) n++;
  if (filters.matched === true || filters.matched === false) n++;
  if ((filters.shop_ids?.length ?? 0) > 0) n++;
  if ((filters.team_ids?.length ?? 0) > 0) n++;
  if ((filters.order_status_ids?.length ?? 0) > 0) n++;
  if ((filters.shipment_status_ids?.length ?? 0) > 0) n++;
  if ((filters.estados?.length ?? 0) > 0) n++;
  if ((filters.weight_compares?.length ?? 0) > 0) n++;
  return n;
}

/** Contador para badge del botón Filtros (sin búsqueda rápida), como Orders `filterConditions.length`. */
export function countSettlementLinesPanelFilters(filters: CourierSettlementLinesFilters): number {
  let n = 0;
  if (filters.matched === true || filters.matched === false) n++;
  if ((filters.shop_ids?.length ?? 0) > 0) n++;
  if ((filters.team_ids?.length ?? 0) > 0) n++;
  if ((filters.order_status_ids?.length ?? 0) > 0) n++;
  if ((filters.shipment_status_ids?.length ?? 0) > 0) n++;
  if ((filters.estados?.length ?? 0) > 0) n++;
  if ((filters.weight_compares?.length ?? 0) > 0) n++;
  return n;
}

export function cloneSettlementLinesFilters(f: CourierSettlementLinesFilters): CourierSettlementLinesFilters {
  return {
    ...(f.search?.trim() ? { search: f.search.trim().slice(0, 255) } : {}),
    ...(f.matched === true || f.matched === false ? { matched: f.matched } : {}),
    ...(f.shop_ids?.length ? { shop_ids: [...f.shop_ids] } : {}),
    ...(f.team_ids?.length ? { team_ids: [...f.team_ids] } : {}),
    ...(f.order_status_ids?.length ? { order_status_ids: [...f.order_status_ids] } : {}),
    ...(f.shipment_status_ids?.length ? { shipment_status_ids: [...f.shipment_status_ids] } : {}),
    ...(f.estados?.length ? { estados: [...f.estados] } : {}),
    ...(f.weight_compares?.length ? { weight_compares: [...f.weight_compares] } : {}),
  };
}
